import { useEffect, useState } from 'react'
import { EmptyFolder, LoadFilesError } from '@/assets/illustration'
import { Retry } from '@/assets/icons'
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs'
import { Button } from '@/components/button/Button'
import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import type { FolderResponse } from '@/user_files/folders/schemas'
import { MatterFilesFolderFileList, type MatterFilesFileToggleOptions } from './MatterFilesFolderFileList'
import type { FolderSelectionState } from './useRecursiveFolderSelection'

const SKELETON_MIN_MS = 300

export type BrowseMatterFilesProps = {
  /** Breadcrumb trail for the current browse path within the matter. */
  breadcrumbItems: BreadcrumbItemType[]
  /** Child folders of the browsed folder. */
  folders: FolderResponse[]
  /** Files in the browsed folder. */
  files: UserFileRead[]
  /** True while the browsed folder’s data is not ready to show the list. */
  loading: boolean
  /** Blocks the file list with empty error UI (illustration + optional Retry), e.g. browse / folder fetch failed. */
  blockingError?: string
  /** Refetch after a blocking browse load failure (`blockingError`). */
  onRetry?: () => void
  /** Optional empty state override. */
  emptyState?: {
    title: string
    description?: string
    action?: {
      label: string
      onClick: () => void
    }
  }
  /** When true, folder row buttons are disabled (e.g. move mutation in flight). File rows unchanged. */
  disabled?: boolean
  /** File ids selected for add-files (checkboxes when `onToggleFile` is set). */
  selectedFileIds?: string[]
  /** When set, file rows are selectable. */
  onToggleFile?: (file: UserFileRead, options?: MatterFilesFileToggleOptions) => void
  /** Controls the input type for selectable rows. Defaults to 'checkbox'. */
  selectionVariant?: 'checkbox' | 'radio'
  /** Optional file-level selection rule. Defaults to allowing every loaded file. */
  isFileSelectable?: (file: UserFileRead) => boolean
  /** Optional tooltip shown for files rejected by `isFileSelectable`. */
  fileNotSelectableMessage?: string
  /** Get recursive selection state for a child folder row. */
  getFolderSelectionState?: (folderId: string) => FolderSelectionState
  /** Get tooltip copy for a child folder's recursive selection state. */
  getFolderSelectionTooltipMessage?: (folderId: string) => string | null
  /** Toggle recursive file selection for a child folder row. */
  onToggleFolderSelection?: (folder: FolderResponse) => void
  /** Open a subfolder by id. */
  onNavigateToFolder: (folderId: string) => void
}

export function BrowseMatterFiles({
  breadcrumbItems,
  disabled = false,
  files,
  folders,
  blockingError,
  loading,
  selectedFileIds,
  onNavigateToFolder,
  onRetry,
  onToggleFile,
  onToggleFolderSelection,
  selectionVariant,
  isFileSelectable,
  fileNotSelectableMessage,
  emptyState,
  getFolderSelectionState,
  getFolderSelectionTooltipMessage,
}: BrowseMatterFilesProps) {
  const [showSkeleton, setShowSkeleton] = useState(false)

  // Set how long we want to dwell on the skeleton (to avoid flickering when loading).
  useEffect(() => {
    if (loading) {
      setShowSkeleton(true)
      return
    }
    const timer = setTimeout(() => setShowSkeleton(false), SKELETON_MIN_MS)
    return () => clearTimeout(timer)
  }, [loading])

  const isEmpty = folders.length === 0 && files.length === 0

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex min-h-12 shrink-0 items-center border-b border-brand-neutral-200 bg-brand-neutral-50 px-6 py-1.5">
        <div className="-ml-1 min-w-0 flex-1">
          <Breadcrumbs items={breadcrumbItems} maxItems={3} persistFirstItem />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {showSkeleton ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain" aria-busy="true">
            <ul className="m-0 list-none divide-y divide-brand-neutral-200 p-0">
              {Array.from({ length: 5 }, (_, i) => (
                <li key={i} className="flex h-12 items-center pl-6 pr-3">
                  <LoadingSkeleton width="100%" />
                </li>
              ))}
            </ul>
          </div>
        ) : blockingError ? (
          <div className="flex min-h-[200px] flex-1 flex-col items-center justify-center gap-4 px-6 pb-6">
            <LoadFilesError className="h-10 w-auto shrink-0 text-true-gray-300" aria-hidden />
            <div className="flex max-w-[292px] flex-col items-center gap-0.5">
              <p className="text-center text-sm font-medium leading-5 tracking-tight text-red-800">{blockingError}</p>
              {onRetry ? (
                <Button
                  type="button"
                  variant="plain"
                  color="secondary"
                  size="sm"
                  trailingIcon={<Retry className="h-4 w-4 shrink-0" aria-hidden />}
                  onClick={onRetry}
                >
                  Retry
                </Button>
              ) : null}
            </div>
          </div>
        ) : isEmpty ? (
          <div className="flex min-h-[200px] flex-1 flex-col items-center justify-center gap-4 px-6 pb-6">
            <EmptyFolder className="h-10 w-auto shrink-0" aria-hidden />
            {emptyState ? (
              <div className="flex max-w-[348px] flex-col items-center gap-1.5 text-center">
                <p className="text-base font-medium leading-6 text-brand-neutral-950">{emptyState.title}</p>
                {emptyState.description ? <p className="text-sm font-medium leading-5 text-brand-neutral-600">{emptyState.description}</p> : null}
                {emptyState.action ? (
                  <Button type="button" variant="plain" color="primary" size="sm" onClick={emptyState.action.onClick}>
                    {emptyState.action.label}
                  </Button>
                ) : null}
              </div>
            ) : (
              <p className="max-w-[292px] text-center text-sm font-medium leading-5 text-brand-neutral-600">There are no subfolders or files in this folder</p>
            )}
          </div>
        ) : (
          <MatterFilesFolderFileList
            disabled={disabled}
            files={files}
            folders={folders}
            getFolderSelectionState={getFolderSelectionState}
            getFolderSelectionTooltipMessage={getFolderSelectionTooltipMessage}
            onNavigateToFolder={onNavigateToFolder}
            onToggleFile={onToggleFile}
            onToggleFolderSelection={onToggleFolderSelection}
            selectedFileIds={selectedFileIds}
            selectionVariant={selectionVariant}
            isFileSelectable={isFileSelectable}
            fileNotSelectableMessage={fileNotSelectableMessage}
          />
        )}
      </div>
    </div>
  )
}
